import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import {
  ConfirmationModal,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Box,
  Button,
  Input,
  Select,
  Stack,
  Switch,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useEmailDisconnect } from "@/queries/integrations/use-emails.query.ts";
import {
  useIntegrationEmailServers,
  useIntegrationEmailServersUpdate,
} from "@/queries/integrations/use-integrations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiEmailServerProtocol,
  ApiEmailServerProtocolItems,
  ApiIntegration,
} from "@/types/integrations/integration.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export interface EmailServersSettingsEditModalProps extends ModalProps {
  integration?: ApiIntegration;
}

export function EmailServersSettingsEditModal({
  integration,
  ...props
}: EmailServersSettingsEditModalProps) {
  const { id } = props;
  const { close, openModal } = useModals();
  const { data: SMTPSettings } = useIntegrationEmailServers(integration?.id);
  const { t } = useTranslation();
  const emailDisconnect = useEmailDisconnect();
  const { showInfo } = useToasts();

  const SMTPFormSchemaObject = yup.object({
    email: yup.string().required(t("errors:field_required")),
    password: yup.string().required(t("errors:field_required")),
    imap_or_pop3_port: yup.number().required(t("errors:field_required")),
    imap_or_pop3_server: yup.string().required(t("errors:field_required")),
    imap_or_pop3_use_ssl: yup.boolean().required(t("errors:field_required")),
    protocol: yup.string().required(t("errors:field_required")),
    smtp_port: yup.number().required(t("errors:field_required")),
    smtp_server: yup.string().required(t("errors:field_required")),
    smtp_use_tls: yup.boolean().required(t("errors:field_required")),
  });

  type SMTPFormSchema = yup.InferType<typeof SMTPFormSchemaObject>;

  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors },
  } = useForm<SMTPFormSchema>({
    mode: "onSubmit",
    resolver: yupResolver(SMTPFormSchemaObject),
    defaultValues: {
      imap_or_pop3_port: 993,
      imap_or_pop3_use_ssl: true,
      smtp_port: 465,
      smtp_use_tls: true,
      protocol: ApiEmailServerProtocol.imap,
    },
  });

  useEffect(() => {
    if (!SMTPSettings) return;

    reset({
      email: SMTPSettings?.email,
      imap_or_pop3_port: SMTPSettings.imap_or_pop3_port,
      imap_or_pop3_server: SMTPSettings.imap_or_pop3_server,
      imap_or_pop3_use_ssl: SMTPSettings.imap_or_pop3_use_ssl,
      protocol: SMTPSettings.protocol,
      smtp_port: SMTPSettings.smtp_port,
      smtp_server: SMTPSettings.smtp_server,
      smtp_use_tls: SMTPSettings.smtp_use_tls,
    });
  }, [SMTPSettings]);

  const save = useIntegrationEmailServersUpdate(control);

  const onSubmit = handleSubmit(async (data) => {
    if (save.isPending) return;

    try {
      const result = await save.mutateAsync({
        id: integration?.id,
        payload: {
          email: data.email,
          password: data.password,
          imap_or_pop3_port: data.imap_or_pop3_port,
          imap_or_pop3_server: data.imap_or_pop3_server,
          imap_or_pop3_use_ssl: data.imap_or_pop3_use_ssl,
          protocol: data.protocol,
          smtp_port: data.smtp_port,
          smtp_server: data.smtp_server,
          smtp_use_tls: data.smtp_use_tls,
        },
      });

      if (result) {
        close(id);
      }
    } catch {}
  });

  const handleDeleteIntegration = () => {
    if (!integration) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("core:question_delete_integration"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        emailDisconnect.mutate(integration.id, {
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.email.disconnected, {
              [analyticProperties.id]: integration.id,
            });
            showInfo({
              title: t("integrations:account_disconnected"),
            });
            close(id);
          },
        });
      },
    });
  };

  return (
    <Modal
      {...props}
      withCloser
      title={t("core:email_servers_settings")}
      actions={
        <Stack gap={"md"}>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} isLoading={save.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"md"}>
        <form onSubmit={onSubmit}>
          <Input {...register("email")} error={errors.email?.message} label={t("core:email")} />
          <Input
            type={"password"}
            {...register("password")}
            error={errors.password?.message}
            label={t("common:password")}
          />

          <Title size={"xs"} header={t("core:email_outgoing_server")} paddingTop />
          <Input
            {...register("smtp_server")}
            error={errors.smtp_server?.message}
            label={t("core:server")}
          />
          <Input
            type={"number"}
            {...register("smtp_port")}
            error={errors.smtp_port?.message}
            label={t("core:port")}
          />
          <Box className={"py-2"}>
            <Controller
              render={({ field: { onChange, value } }) => (
                <Switch size={"sm"} checked={value} onChange={onChange} title={t("core:use_ssl")} />
              )}
              name={"smtp_use_tls"}
              control={control}
            />
          </Box>

          <Title size={"xs"} header={t("core:email_income_server")} paddingTop />
          <Controller
            render={({ field: { onChange, value } }) => (
              <Select
                emptyTitle={t("common:select")}
                value={value}
                options={ApiEmailServerProtocolItems}
                onChange={onChange}
              />
            )}
            name={"protocol"}
            control={control}
          />
          <Input
            {...register("imap_or_pop3_server")}
            error={errors.imap_or_pop3_server?.message}
            label={t("core:server")}
          />
          <Input
            type={"number"}
            {...register("imap_or_pop3_port")}
            error={errors.imap_or_pop3_port?.message}
            label={t("core:port")}
          />
          <Box className={"py-2"}>
            <Controller
              render={({ field: { onChange, value } }) => (
                <Switch size={"sm"} checked={value} onChange={onChange} title={t("core:use_ssl")} />
              )}
              name={"imap_or_pop3_use_ssl"}
              control={control}
            />
          </Box>
        </form>
        {integration && (
          <DeleteListItem
            onDelete={handleDeleteIntegration}
            title={t("core:question_delete_integration")}
          />
        )}
      </Stack>
    </Modal>
  );
}
